import { useState, useEffect, useRef } from "react";

interface WindowDimensions {
  windowWidth: number;
  windowHeight: number;
}

const useWindowDimensions = (): WindowDimensions => {
  const [windowDimensions, setWindowDimensions] = useState<WindowDimensions>({
    windowWidth: typeof window !== "undefined" ? window.innerWidth : 0,
    windowHeight: typeof window !== "undefined" ? window.innerHeight : 0,
  });

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleResize = () => {
      // Debounce resize events (300ms) to avoid rapid state updates
      // from mobile browser URL bar show/hide during scroll
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        setWindowDimensions({
          windowWidth: window.innerWidth,
          windowHeight: window.innerHeight,
        });
      }, 300);
    };

    window.addEventListener("resize", handleResize);

    // Call handler right away so state gets updated with initial window size
    setWindowDimensions({
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
    });

    // Remove event listener on cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []); // Empty array ensures that effect is only run on mount and unmount

  return windowDimensions;
};

export default useWindowDimensions;
